import { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, useMediaQuery, useTheme, Typography } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Panel } from './Panel';
import { Selection } from './orgNavTypes';
import { useOrgNavState } from './useOrgNavState';
import { OrgTable } from './Tables/OrgTable';
import { SystemTable } from './Tables/SystemTable/SystemTable';
import { EmployeesTable } from './Tables/EmployeesTable';
import type { SystemFilter } from '@/types/systems';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { SystemAssigned } from '@consts/systems';
import { BreadcrumbSubBar } from '@components/BreadcrumbSubBar';
import { FiltersDrawer } from './FiltersDrawer';
import { colors, typography as typographyTokens, radii, Button } from '@components/design-system';

/**
 * Table with tabs for each systems, locations, resellers, companies
 *
 * Org entities only use main org filters, for orgs since all of the data already exists locally, we should use a fake pagination of 30 elements per page
 * Systems use main org filters + extra filters, they do not exist locally so actual pagination is required, should use same pagination # as Org
 *
 * Due to this difference in behavior, we should split this into 2 seperate components:
 * - OrgTable
 * - SystemTable
 *
 * All behavior which describes the datatable + data loading should be encapsulated here.
 * The main org filters are shared, so should be passed down as props to both components.
 */

export type FilterInput = {
    companies: { [key: string]: boolean };
    resellers: { [key: string]: boolean };
    locations: { [key: string]: boolean };
};

// Selection tab name mapping for mobile header
const TAB_NAMES: Record<Selection, string> = {
    [Selection.Company]: 'Companies',
    [Selection.Reseller]: 'Resellers',
    [Selection.Location]: 'Locations',
    [Selection.Employee]: 'Employees',
    [Selection.System]: 'Systems',
};

export const OrgNav = () => {
    const [selection, setSelection] = useOrgNavState();

    // Mobile detection
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

    // Current user's role utilities
    const [searchParams, setSearchParams] = useSearchParams();

    const tabParam = (searchParams.get('tab') as Selection) || Selection.Company;

    const [selectedTab, setSelectedTab] = useState<Selection>(tabParam);

    // Sync tab state to URL whenever it changes
    useEffect(() => {
        if (searchParams.get('tab') !== selectedTab) {
            searchParams.set('tab', selectedTab);
            setSearchParams(searchParams);
        }
    }, [selectedTab]);

    const [orgFilters, setOrgFilters] = useState<FilterInput>({
        companies: {},
        resellers: {},
        locations: {},
    });

    const [systemFilters, setSystemFilters] = useState<SystemFilter>({
        model: {},
        status: {},
    });

    const [assigned, setAssigned] = useState(SystemAssigned.Assigned);

    // 'active' = only active, 'archived' = only archived
    const [locationStatus, setLocationStatus] = useState<'active' | 'archived'>('active');

    const [deleteEmployee, setDeleteEmployee] = useState<any>(null);

    const [searchTerm, setSearchTerm] = useLocalStorage<string>({
        key: 'systems_table_search_term',
        value: '',
    });

    const setOrgFilter = (key: keyof FilterInput, value: string, selectOne?: boolean) => {
        const isActive = orgFilters[key][value];
        const isRemoving = isActive; // If currently active, we're removing it

        const updatedFilters = { ...orgFilters, [key]: { ...(selectOne ? {} : orgFilters[key]), [value]: !isActive } };

        // Cascade clear children only when removing a parent filter (subtraction)
        if (isRemoving) {
            if (key === 'companies') {
                // When company is removed, clear reseller and location selections
                updatedFilters.resellers = {};
                updatedFilters.locations = {};
            } else if (key === 'resellers') {
                // When reseller is removed, clear location selections
                updatedFilters.locations = {};
            }
        }

        setOrgFilters(updatedFilters);
    };

    const setSystemFilter = (key: keyof SystemFilter, value: string, selectOne?: boolean) => {
        const isActive = systemFilters[key][value];
        setSystemFilters({ ...systemFilters, [key]: { ...(selectOne ? {} : systemFilters[key]), [value]: !isActive } });
    };

    const filterParams = useMemo(() => {
        const { companies, resellers, locations } = orgFilters;
        const companyIds = Object.keys(companies)
            .filter((key) => companies[key])
            .join(',');
        const resellerIds = Object.keys(resellers)
            .filter((key) => resellers[key])
            .join(',');
        const locationIds = Object.keys(locations)
            .filter((key) => locations[key])
            .join(',');
        return {
            companyIds,
            resellerIds,
            locationIds,
            includeInactive: locationStatus === 'archived' ? 'only' : 'false',
        };
    }, [orgFilters, locationStatus]);

    const handleOrgClear = () => {
        setOrgFilters({
            companies: {},
            resellers: {},
            locations: {},
        });
    };

    const handleSystemClear = () => {
        setSystemFilters({
            model: {},
            status: {},
        });
        setSearchTerm('');
        setAssigned(SystemAssigned.Assigned);
    };

    const handleFilterChange = (newFilters: FilterInput, newTab: Selection) => {
        setOrgFilters(newFilters);
        setSelectedTab(newTab);
    };

    return (
        <>
            <BreadcrumbSubBar />

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    height: '100%',
                    overflow: 'hidden',
                    boxSizing: 'border-box',
                }}>
                {/* Mobile Header Bar */}
                {isMobile && !selection.type && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1.5,
                            px: 2,
                            py: 1.5,
                            backgroundColor: colors.neutral[0],
                            borderBottom: `1px solid ${colors.neutral[200]}`,
                            flexShrink: 0,
                        }}>
                        <Typography
                            sx={{
                                fontSize: typographyTokens.fontSize.lg,
                                fontWeight: typographyTokens.fontWeight.semibold,
                                color: colors.neutral[900],
                            }}>
                            {TAB_NAMES[selectedTab]}
                        </Typography>
                        <Button
                            variant='secondary'
                            size='small'
                            onClick={() => setMobileDrawerOpen(true)}
                            startIcon={<FilterListIcon />}
                            sx={{
                                minWidth: 'auto',
                                px: 1,
                                '& .MuiButton-startIcon': {
                                    margin: 0,
                                },
                            }}>
                            {''}
                        </Button>
                    </Box>
                )}

                {/* Main layout container */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        flex: 1,
                        minHeight: 0,
                        overflow: 'hidden',
                    }}>
                    {/* Filters Panel - Hidden on mobile when selection.type is set */}
                    <FiltersDrawer
                        selectedTab={selectedTab}
                        setSelectedTab={(nextTab) => {
                            setSelection({ type: null, id: null });
                            // Reset search term when switching tabs to avoid stale state
                            setSearchTerm('');
                            setSelectedTab(nextTab);
                        }}
                        orgFilters={orgFilters}
                        setOrgFilter={setOrgFilter}
                        systemFilters={systemFilters}
                        setSystemFilter={setSystemFilter}
                        assigned={assigned}
                        setAssigned={setAssigned}
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        locationStatus={locationStatus}
                        setLocationStatus={setLocationStatus}
                        onClearOrg={handleOrgClear}
                        onClearSystem={handleSystemClear}
                        onCreate={(type) => {
                            setSelection({ type, id: null });
                        }}
                        isMobile={isMobile}
                        mobileOpen={mobileDrawerOpen}
                        onMobileClose={() => setMobileDrawerOpen(false)}
                    />

                    {/* Main Content */}
                    <Box
                        display='flex'
                        flexDirection='column'
                        flex={1}
                        sx={{
                            minWidth: 0,
                            minHeight: 0, // Safari flex fix
                            overflowY: 'hidden',
                            overflowX: 'hidden',
                        }}>
                        {/* Data / Tables area */}
                        {!selection.type && (
                            <Box
                                sx={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    minHeight: 0,
                                    overflow: 'hidden',
                                    backgroundColor: '#F4F6F8',
                                }}>
                                {[Selection.Company, Selection.Reseller, Selection.Location].includes(selectedTab) && (
                                    <OrgTable
                                        tab={selectedTab as Selection.Company | Selection.Reseller | Selection.Location}
                                        orgFilters={filterParams}
                                        onFilterChange={handleFilterChange}
                                        isMobile={isMobile}
                                    />
                                )}

                                {selectedTab === Selection.Employee && (
                                    <EmployeesTable
                                        orgFilters={filterParams}
                                        searchText={searchTerm}
                                        onSelect={(e) => setSelection({ type: Selection.Employee, id: e.EmployeeID! })}
                                        onDelete={(e) => setDeleteEmployee(e)}
                                    />
                                )}

                                {selectedTab === Selection.System && (
                                    <SystemTable
                                        assigned={assigned}
                                        filters={systemFilters}
                                        orgFilters={filterParams}
                                        searchTerm={searchTerm}
                                    />
                                )}
                            </Box>
                        )}

                        {selection.type && (
                            <Box
                                display={'flex'}
                                flexDirection={'column'}
                                width={'100%'}
                                flex={1}
                                sx={{
                                    minHeight: 0,
                                    // Safari fix: remove height: 100%, rely on flex: 1
                                    overflow: 'auto',
                                    // Ensure Safari knows this is a containing block
                                    position: 'relative',
                                }}>
                                <Panel
                                    selection={selection}
                                    onSuccess={() => {
                                        setSelection({
                                            type: null,
                                            id: null,
                                        });
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </>
    );
};
